import { HStack, Icon, VStack } from '@musinsa/mwms-ds'
import Text from '@/components/content/Text'
import { borderRadiusSystem, colorSystem } from '@/styles/theme'
import { Utils } from '@/utils/Utils'
import { ReactNode, useState } from 'react'
import styled from 'styled-components'
import { pick } from 'lodash-es'
import { Amount, SettlementAmountSummaryRes } from '@/services/settlement/settlementManagement/types'
import { useMeasurementSettlementEnabled } from '@/pages/settlement/SettlementManagement/hooks/useMeasurementSettlementEnabled'

type SettlementKey = Extract<
  keyof SettlementAmountSummaryRes,
  | 'inventoryAmount'
  | 'inboundAmount'
  | 'customerOutboundAmount'
  | 'storeOutboundAmount'
  | 'returnInboundAmount'
  | 'measurementAmount'
  | 'etcAdjustmentAmount'
>

const SETTLEMENT_LABEL_MAP: {
  [key in SettlementKey]: string
} = {
  inventoryAmount: '보관비',
  inboundAmount: '입고비',
  customerOutboundAmount: '고객출고비',
  storeOutboundAmount: '매장출고비',
  returnInboundAmount: '반품비',
  // releaseAmount: '반출비',
  measurementAmount: '실측비',
  etcAdjustmentAmount: '기타조정비',
}

interface SettlementSummaryProps {
  title: ReactNode
  description?: ReactNode
  rightArea?: ReactNode
  settlementAmounts?: SettlementAmountSummaryRes
  /**
   * 프로모션 할인 합계 — 프로모션 집계 합산값. 총액 역산인 promotionDiscountedAmount 가 아니다 (LOGISPRD-11385).
   * null = BE 노출 FF(feature.settlementSavings) off. 0 과 구분해야 하므로 ?? 0 으로 뭉개지 않는다
   */
  promotionSavedAmount?: number | null
  /** 합배송으로 아낀 금액 (LOGISPRD-10076). null = BE 노출 FF off */
  combinedShippingSavedAmount?: number | null
}
export function SettlementSummary({
  title,
  description,
  rightArea,
  settlementAmounts,
  promotionSavedAmount,
  combinedShippingSavedAmount,
}: SettlementSummaryProps) {
  const [benefitOpen, setBenefitOpen] = useState(false)
  // LOGISPRD-16794 실측비 타일은 flag ON 에서만 보인다
  const isMeasurementEnabled = useMeasurementSettlementEnabled()
  const displayKeys = (Object.keys(SETTLEMENT_LABEL_MAP) as SettlementKey[]).filter((key) => isMeasurementEnabled || key !== 'measurementAmount')

  // 시안 02_파트너정산_프로모션할인_v2.html:196 — 합배송 절약 + 프로모션 할인
  const promotionSaved = promotionSavedAmount ?? 0
  const combinedSaved = combinedShippingSavedAmount ?? 0
  const benefitTotal = promotionSaved + combinedSaved
  // 금액이 0원이어도 표시한다 — 합계로 가르면 화주·월에 따라 혜택 칸이 보이다 말다 한다.
  // 노출 자체는 BE FF 가 가르고, off 면 두 값이 다 null 로 온다
  const benefitVisible = promotionSavedAmount != null || combinedShippingSavedAmount != null

  // 실측비가 없는 응답(BE 미배포)이어도 타일은 0원으로 보인다
  const amounts = settlementAmounts && {
    ...pick(settlementAmounts, displayKeys),
    measurementAmount: settlementAmounts.measurementAmount ?? { originAmount: 0, discountedAmount: 0 },
  }
  const settlementItems = amounts ? displayKeys.map((key) => [key, amounts[key]] as [SettlementKey, number | Amount]) : []

  return (
    <SettlementSummaryWrapper>
      <HStack justify="space-between">
        <VStack gap={8}>
          <Text style={{ fontSize: '20px', lineHeight: '32px', fontWeight: 500 }}>{title}</Text>
          {description}
        </VStack>
        {rightArea}
      </HStack>
      <HStack style={{ gap: 100, alignItems: 'start' }}>
        <Text kind="Display_32_bold" style={{ margin: '8px 0', alignItems: 'baseline', minWidth: 280 }}>
          {Utils.getCommaNum(settlementAmounts?.totalAmount.discountedAmount ?? 0)}
          <Text kind="Headline_16_bold">원</Text>
        </Text>
        <GridContainer $columnCount={settlementItems.length}>
          {settlementItems.map(([name, amounts]) => {
            // MEMO: 기타조정비에 경우 프로모션 대상에서 제외되어 discountedAmount없이 number 타입으로 전달됨.
            if (typeof amounts === 'number') {
              return (
                <VStack key={name}>
                  <Text kind="Body_14_regular">{SETTLEMENT_LABEL_MAP[name]}</Text>
                  <Text style={{ marginTop: 8, fontSize: '16px', lineHeight: '24px', fontWeight: 500 }}>{Utils.getCommaNum(amounts)}원</Text>
                </VStack>
              )
            }

            const { originAmount, discountedAmount } = amounts

            return (
              <VStack key={name}>
                <Text kind="Body_14_regular">{SETTLEMENT_LABEL_MAP[name]}</Text>
                <Text style={{ marginTop: 8, fontSize: '16px', lineHeight: '24px', fontWeight: 500 }}>
                  {Utils.getCommaNum(discountedAmount ?? 0)}원
                </Text>
                {Boolean(originAmount) && discountedAmount !== originAmount && (
                  <Text kind="Body_14_regular" color={colorSystem.text.common.tertiary} style={{ marginTop: 2, textDecoration: 'line-through' }}>
                    {Utils.getCommaNum(originAmount)}원
                  </Text>
                )}
              </VStack>
            )
          })}
        </GridContainer>
        {benefitVisible && (
          <BenefitBlock>
            <Text kind="Body_14_regular" color={colorSystem.text.common.link}>
              이번달 받은 혜택
            </Text>
            <BenefitAmount type="button" onClick={() => setBenefitOpen((open) => !open)}>
              -{Utils.getCommaNum(benefitTotal)}원
              <Icon name={benefitOpen ? 'arrow-up-filled' : 'arrow-down-filled'} size={12} />
            </BenefitAmount>
            {benefitOpen && (
              <>
                <PopoverBackdrop onClick={() => setBenefitOpen(false)} />
                <BenefitPopover>
                  <Text kind="Body_12_bold">상세정보</Text>
                  <BenefitRow>
                    <Text kind="Body_14_regular">합배송 절약</Text>
                    <Text kind="Body_14_regular" color={colorSystem.text.common.link}>
                      -{Utils.getCommaNum(combinedSaved)}원
                    </Text>
                  </BenefitRow>
                  <BenefitRow>
                    <Text kind="Body_14_regular">프로모션 할인</Text>
                    <Text kind="Body_14_regular" color={colorSystem.text.common.link}>
                      -{Utils.getCommaNum(promotionSaved)}원
                    </Text>
                  </BenefitRow>
                </BenefitPopover>
              </>
            )}
          </BenefitBlock>
        )}
      </HStack>
    </SettlementSummaryWrapper>
  )
}

const SettlementSummaryWrapper = styled(VStack)`
  gap: 8px;
  padding: 24px;
  border: 1px solid ${colorSystem.border.element};
  border-radius: ${borderRadiusSystem.md}px;
`

// 받은 혜택(합배송 절약 + 프로모션 할인) — 정산 항목들과 구분선으로 가른다 (시안: 항목 오른쪽 끝)
const BenefitBlock = styled(VStack)`
  position: relative;
  flex: 0 0 auto;
  padding-left: 24px;
  border-left: 1px solid ${colorSystem.border.element};
`

const BenefitAmount = styled.button`
  display: flex;
  align-items: center;
  gap: 2px;
  margin-top: 8px;
  padding: 0;
  border: 0;
  background: none;
  color: ${colorSystem.text.common.link};
  font-size: 16px;
  line-height: 24px;
  font-weight: 500;
  cursor: pointer;
`

// 팝오버 밖을 누르면 닫는다. 문서 전역 리스너 대신 투명 오버레이 (다른 팝업과 리스너 순서를 다투지 않는다).
// z-index 0 은 사이드바·헤더(z-index 1) 아래라, 그쪽 클릭은 팝오버만 닫히고 삼켜지지 않는다
const PopoverBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 0;
`

const BenefitPopover = styled(VStack)`
  position: absolute;
  top: 64px;
  right: 0;
  /* PopoverBackdrop(0) 위에 놓여야 팝오버 안이 눌린다 */
  z-index: 1;
  gap: 8px;
  /* 금액 9자리(수억원)까지 한 줄로 들어가도록 확보 (LOGISPRD-11505) */
  min-width: 240px;
  padding: 12px 16px;
  border: 1px solid ${colorSystem.border.element};
  border-radius: ${borderRadiusSystem.sm}px;
  background: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`

const BenefitRow = styled(HStack)`
  justify-content: space-between;
  gap: 24px;
  /* 금액 자릿수가 커도 줄바꿈으로 접히지 않게 — 접히면 팝오버 안에서 두 줄로 깨진다 (LOGISPRD-11505) */
  white-space: nowrap;
`


const GridContainer = styled.div<{ $columnCount: number }>`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  row-gap: 24px;
  width: 100%;
  max-width: ${({ $columnCount }) => $columnCount * 240}px;
  overflow: hidden;
`
